import { CSSProperties, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { mainButton } from '@telegram-apps/sdk-react';

import {
  containerStyle,
  inputContainerStyle,
  listItemStyle,
  scrollableListStyle,
  selectedItemMarkStyle,
  selectedItemStyle,
  unselectedItemStyle
} from '@/pages/Profile/Edit/EditEmployment/styles.ts';
import {
  iconCircleStyle,
  innerCircleStyle,
  svgContainerStyle,
  titleStyle
} from '@/pages/Profile/Edit/styles.ts';
import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { EmploymentSector } from '@/enums/apiEnums.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { helpers } from '@/helpers.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import BriefcaseShape from '@/components/common/svg/shapes/BriefcaseShape.tsx';

export const getEmploymentSectors = (
  t: TFunction
): Record<EmploymentSector, string> => ({
  [EmploymentSector.EMPLOYMENT_SECTOR_UNSPECIFIED]: t('status_unspecified'),
  [EmploymentSector.EMPLOYMENT_SECTOR_HEALTH_CARE]: t(
    'employment_sector.health_care'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_MEDIA]: t('employment_sector.media'),
  [EmploymentSector.EMPLOYMENT_SECTOR_IT]: t('employment_sector.it'),
  [EmploymentSector.EMPLOYMENT_SECTOR_TRANSPORTATION_SERVICES]: t(
    'employment_sector.transportation_services'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_MANUFACTURE]: t(
    'employment_sector.manufacture'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_MANAGEMENT]: t(
    'employment_sector.management'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_DEFENSE]: t('employment_sector.defense'),
  [EmploymentSector.EMPLOYMENT_SECTOR_CONSTRUCTION]: t(
    'employment_sector.construction'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_SERVICE]: t('employment_sector.service'),
  [EmploymentSector.EMPLOYMENT_SECTOR_FINANCE]: t('employment_sector.finance'),
  [EmploymentSector.EMPLOYMENT_SECTOR_EDUCATION]: t(
    'employment_sector.education'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_AGRICULTURE]: t(
    'employment_sector.agriculture'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_ARCHITECTURE]: t(
    'employment_sector.architecture'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_ARTS_AND_DESIGN]: t(
    'employment_sector.arts_and_design'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_ENERGY_INDUSTRY]: t(
    'employment_sector.energy_industry'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_ENGINEERING]: t(
    'employment_sector.engineering'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_GOVERNMENT]: t(
    'employment_sector.government'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_HOSPITALITY]: t(
    'employment_sector.hospitality'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_SECURITY_INDUSTRY]: t(
    'employment_sector.security_industry'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_SOCIAL_SERVICES]: t(
    'employment_sector.social_services'
  ),
  [EmploymentSector.EMPLOYMENT_SECTOR_SPORTS]: t('employment_sector.sports'),
  [EmploymentSector.EMPLOYMENT_SECTOR_TOURISM]: t('employment_sector.tourism'),
  [EmploymentSector.EMPLOYMENT_SECTOR_OTHER]: t('employment_sector.other')
});

interface EditEmploymentPopupProps {
  handleClose: () => void;
}

const EditEmploymentPopup: FC<EditEmploymentPopupProps> = ({ handleClose }) => {
  useBackButtonToCloseModal(handleClose, false);

  const { t } = useTranslation();
  const { aboutMe, updateProfile } = useProfileStore();
  const employmentSectors = getEmploymentSectors(t);

  const [isEdited, setIsEdited] = useState<boolean>(false);

  const [selectedEmploymentSector, setSelectedEmploymentSector] =
    useState<EmploymentSector>(
      aboutMe?.profile.employment_sector ??
        EmploymentSector.EMPLOYMENT_SECTOR_UNSPECIFIED
    );

  const selectEmploymentSector = (value: EmploymentSector) => {
    setIsEdited(true);
    setSelectedEmploymentSector(value);
  };

  const submitEmploymentSector = async () => {
    mainButton.setParams({ isLoaderVisible: true, isEnabled: false });

    try {
      await updateProfile({ employment_sector: selectedEmploymentSector });
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
      });
      handleClose();
    } catch {
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_ERROR,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} />
      });
    } finally {
      mainButton.setParams({
        isLoaderVisible: false,
        isVisible: false,
        isEnabled: true
      });
    }
  };

  useEffect(() => {
    mainButton.setParams({
      isVisible: isEdited
    });
  }, [isEdited]);

  useEffect(() => {
    mainButton.setParams({
      isEnabled: true,
      backgroundColor: '#000000',
      text: t('button.save')
    });

    mainButton.onClick(submitEmploymentSector);
    return () => mainButton.offClick(submitEmploymentSector);
  }, [selectedEmploymentSector]);

  return (
    <div style={popupContainerStyle}>
      <div style={svgContainerStyle}>
        <div style={iconCircleStyle}>
          <div style={innerCircleStyle}>
            <BriefcaseShape width={25} height={25} fillColor="#fff" />
          </div>
        </div>
        <div style={titleStyle}>{t('indicate_your_employment')}</div>
      </div>

      <div style={inputContainerStyle}>
        <InputContent>
          <div style={containerStyle}>
            <div style={scrollableListStyle}>
              {Object.entries(employmentSectors).map(([value, label]) => (
                <div
                  key={value}
                  onClick={() =>
                    selectEmploymentSector(value as EmploymentSector)
                  }
                  style={{
                    ...listItemStyle,
                    ...(selectedEmploymentSector === (value as EmploymentSector)
                      ? selectedItemStyle
                      : unselectedItemStyle),
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{label}</span>
                  {selectedEmploymentSector === (value as EmploymentSector) && (
                    <span style={selectedItemMarkStyle}>✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </InputContent>
      </div>
    </div>
  );
};

const popupContainerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'auto'
};

export default EditEmploymentPopup;
